import {
  CASE_SOURCES,
  type CaseDto,
  type CaseSource,
  LOST_REASONS,
  type LostReason,
  PRIORITIES,
  type PartyDto,
  type Priority,
  type ScopeDto,
  type CaseCloseCheckDto,
  datePl,
  type HeldWorkDto,
  type WorkHoldPreviewDto,
  type OfferListItemDto,
  type ShareDto,
} from '@bqcrm/shared';
import { Alert, Button, Checkbox, Group, List, Select, SimpleGrid, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { IconCircleCheckFilled, IconCircleXFilled } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notifyOk } from '../../notify';
import { useState } from 'react';
import { api } from '../../api';
import { PartyPicker } from '../../components/PartyPicker';
import { QueryState } from '../../components/QueryState';
import { useCaseMutation, useCompanies, useOwnerCandidates, useTrades } from '../../data';
import { can, useMe } from '../../auth';
import { minHint } from '../../minHint';

const opts = (o: Record<string, string>) => Object.entries(o).map(([value, label]) => ({ value, label }));
const Actions = ({ onCancel, onOk, loading, okLabel = 'Zapisz', disabled, danger }: { onCancel: () => void; onOk: () => void; loading: boolean; okLabel?: string; disabled?: boolean; danger?: boolean }) => (
  <Group justify="flex-end" mt="md">
    <Button variant="default" onClick={onCancel}>
      Anuluj
    </Button>
    <Button color={danger ? 'red' : undefined} onClick={onOk} loading={loading} disabled={disabled}>
      {okLabel}
    </Button>
  </Group>
);

export function EditCaseForm({ c, onDone }: { c: CaseDto; onDone: () => void }) {
  const client = useQuery({ queryKey: ['party', c.client.id], queryFn: () => api<PartyDto>(`/parties/${c.client.id}`) });
  const [f, setF] = useState({
    title: c.title,
    description: c.description,
    source: c.source,
    priority: c.priority,
    investorId: c.investor?.id ?? null,
    onsiteContactId: c.onsiteContact?.id ?? null,
    nextAction: c.nextAction,
    nextActionDate: c.nextActionDate,
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const m = useCaseMutation(c.id, () => api<CaseDto>(`/cases/${c.id}`, { method: 'PUT', body: f }), () => {
    notifyOk('Zapisano dane sprawy');
    onDone();
  });

  return (
    <Stack>
      <TextInput label="Tytuł" value={f.title} onChange={(e) => set('title', e.currentTarget.value)} />
      <Textarea label="Opis potrzeby" autosize minRows={3} value={f.description} onChange={(e) => set('description', e.currentTarget.value)} />
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Select label="Źródło" data={opts(CASE_SOURCES)} value={f.source} onChange={(v) => v && set('source', v as CaseSource)} allowDeselect={false} />
        <Select label="Priorytet" data={opts(PRIORITIES)} value={f.priority} onChange={(v) => v && set('priority', v as Priority)} allowDeselect={false} />
      </SimpleGrid>
      <PartyPicker label="Inwestor (jeżeli inny niż klient)" value={f.investorId} initialLabel={c.investor?.displayName} onChange={(v) => set('investorId', v)} />
      <Select
        label="Kontakt na miejscu"
        description="Osoba widoczna dla ekipy. Brak wyboru = klient."
        data={(client.data?.contacts ?? []).map((ct) => ({ value: ct.id, label: `${ct.fullName}${ct.role ? ` (${ct.role})` : ''}${ct.phone ? ` · ${ct.phone}` : ''}` }))}
        value={f.onsiteContactId}
        onChange={(v) => set('onsiteContactId', v)}
        clearable
        placeholder={`${c.client.displayName} (klient)`}
      />
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <TextInput label="Następny krok" value={f.nextAction} onChange={(e) => set('nextAction', e.currentTarget.value)} placeholder="np. oddzwonić z terminem wizji" />
        <TextInput label="Termin" type="date" value={f.nextActionDate ?? ''} onChange={(e) => set('nextActionDate', e.currentTarget.value || null)} />
      </SimpleGrid>
      <Actions onCancel={onDone} onOk={() => m.mutate(undefined)} loading={m.isPending} />
    </Stack>
  );
}

export function OwnerForm({ c, onDone }: { c: CaseDto; onDone: () => void }) {
  const users = useOwnerCandidates();
  const [ownerId, setOwnerId] = useState<string | null>(c.owner?.id ?? null);
  // obecny opiekun spoza listy (np. bez funkcji „opiekun”) — pole puste, trzeba wybrać osobę z listy
  const listed = !!ownerId && !!users.data?.some((u) => u.id === ownerId);
  const m = useCaseMutation(c.id, () => api<CaseDto>(`/cases/${c.id}/owner`, { method: 'PUT', body: { ownerId } }), () => {
    notifyOk('Zmieniono opiekuna sprawy');
    onDone();
  });
  return (
    <Stack>
      <Select label="Opiekun sprawy" description="Osoby z funkcją „opiekun” — mogą nadać sprawie bieg" data={(users.data ?? []).map((u) => ({ value: u.id, label: u.fullName }))} value={listed ? ownerId : null} onChange={setOwnerId} allowDeselect={false} />
      <Actions onCancel={onDone} onOk={() => m.mutate(undefined)} loading={m.isPending} disabled={!listed || ownerId === c.owner?.id} />
    </Stack>
  );
}

/** Warunki zamknięcia sprawy (docs/10 §3.7): spełnione / niespełnione z listą braków. */
export function CloseConditions({ check }: { check: CaseCloseCheckDto }) {
  return (
    <Stack gap={6}>
      {check.conditions.map((x) => (
        <div key={x.key}>
          <Group gap={6} wrap="nowrap">
            {x.ok ? (
              <IconCircleCheckFilled size={16} color="var(--mantine-color-green-6)" style={{ flexShrink: 0 }} />
            ) : (
              <IconCircleXFilled size={16} color="var(--mantine-color-red-6)" style={{ flexShrink: 0 }} />
            )}
            <Text size="sm" fw={x.ok ? 400 : 600}>
              {x.label}
            </Text>
          </Group>
          {!x.ok && (
            <List size="xs" spacing={0} ml={22} c="dimmed">
              {x.details.slice(0, 8).map((d) => (
                <List.Item key={d}>{d}</List.Item>
              ))}
              {x.details.length > 8 && <List.Item>…i {x.details.length - 8} więcej</List.Item>}
            </List>
          )}
        </div>
      ))}
    </Stack>
  );
}

/**
 * Zamknięcie sprawy jako zrealizowanej: lista warunków, weryfikacja końcowa opiekuna, zamknięcie
 * (gdy warunki spełnione) albo wyjątkowe — z uprawnieniem i uzasadnieniem. Po weryfikacji sprawa zamyka się sama,
 * gdy dojdzie ostatni brakujący warunek (np. wpłata).
 */
export function CompleteForm({ c, onDone }: { c: CaseDto; onDone: () => void }) {
  const qc = useQueryClient();
  const check = useQuery({ queryKey: ['case-completion', c.id], queryFn: () => api<CaseCloseCheckDto>(`/cases/${c.id}/completion`) });
  const [note, setNote] = useState('');
  const [exceptional, setExceptional] = useState(false);
  const [reason, setReason] = useState('');
  const after = (msg: string) => () => {
    void qc.invalidateQueries({ queryKey: ['case-timeline', c.id] });
    void qc.invalidateQueries({ queryKey: ['case-completion', c.id] });
    void qc.invalidateQueries({ queryKey: ['case-process', c.id] });
    notifyOk(msg);
    onDone();
  };
  const verify = useCaseMutation(c.id, () => api<CaseDto>(`/cases/${c.id}/final-verification`, { method: 'POST', body: { note } }), after('Weryfikacja końcowa potwierdzona'));
  const close = useCaseMutation(c.id, () => api<CaseDto>(`/cases/${c.id}/complete`, { method: 'POST', body: { note, exceptional, reason } }), after(exceptional ? 'Sprawa zamknięta wyjątkowo' : 'Sprawa zamknięta jako zrealizowana'));
  const r = check.data;
  return (
    <Stack>
      <QueryState isLoading={check.isLoading} error={check.error} lines={3}>
      {r && (
        <>
          <CloseConditions check={r} />
          {r.verified ? (
            <Alert color="eko" variant="light">
              Weryfikację końcową potwierdził(a) {r.verified.byName ?? '—'}{r.verified.note ? `: „${r.verified.note}”` : ''}.{' '}
              {r.ready ? 'Wszystkie warunki spełnione.' : 'Sprawa zamknie się sama, gdy pozostałe warunki będą spełnione.'}
            </Alert>
          ) : r.worksDone ? (
            <Text size="sm" c="dimmed">
              Prace i odbiory zakończone. Potwierdź weryfikację — jeśli coś jeszcze czeka (np. wpłata), sprawa zamknie się sama, gdy się dopełni.
            </Text>
          ) : null}
          <Textarea label="Notatka (opcjonalnie)" placeholder="np. dokumenty kompletne, klient zadowolony" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} maxLength={500} />
          {r.can.closeExceptional && !r.ready && (
            <Checkbox label="Zamknięcie wyjątkowe — mimo niespełnionych warunków" checked={exceptional} onChange={(e) => setExceptional(e.currentTarget.checked)} />
          )}
          {exceptional && (
            <Textarea
              label="Uzasadnienie zamknięcia wyjątkowego"
              description="Zostanie zapisane w historii razem z listą niespełnionych warunków."
              required
              autosize
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onDone}>
              Anuluj
            </Button>
            {r.can.verify && !exceptional && (
              <Button variant={r.ready ? 'light' : 'filled'} loading={verify.isPending} onClick={() => verify.mutate(undefined)}>
                Potwierdź weryfikację końcową
              </Button>
            )}
            {exceptional ? (
              <Button color="red" disabled={reason.trim().length < 10} loading={close.isPending} onClick={() => close.mutate(undefined)}>
                Zamknij wyjątkowo
              </Button>
            ) : (
              <Button disabled={!r.can.close} loading={close.isPending} onClick={() => close.mutate(undefined)}>
                Zamknij sprawę
              </Button>
            )}
          </Group>
        </>
      )}
      </QueryState>
    </Stack>
  );
}

/** Ponowne otwarcie zamkniętej sprawy — z powodem (wymagany dla zrealizowanej). */
export function ReopenForm({ c, onDone }: { c: CaseDto; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const m = useCaseMutation(c.id, () => api<CaseDto>(`/cases/${c.id}/reopen`, { method: 'POST', body: { reason } }), () => {
    notifyOk('Sprawa otwarta ponownie');
    onDone();
  });
  const required = c.closeKind === 'zrealizowana';
  return (
    <Stack>
      <Text size="sm">Sprawa wróci do pracy; {required ? 'weryfikację końcową trzeba będzie potwierdzić ponownie.' : 'zgłoszenie wróci do weryfikacji wstępnej.'}</Text>
      <Textarea label="Powód" required={required} autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} data-autofocus />
      <Actions onCancel={onDone} onOk={() => m.mutate(undefined)} loading={m.isPending} okLabel="Otwórz ponownie" disabled={required && reason.trim().length < 3} />
    </Stack>
  );
}

/**
 * P34: „Powiadom klienta” w oknach zamknięcia (bez adresu e-mail klienta pole jest wyłączone). QA 10302046 P2: standardowa
 * wiadomość jest neutralna — bez powodu i „Szczegółów” (notatka wewnętrzna); pod polem treści — jej podgląd.
 */
function NotifyClient({ c, kind, value, onChange, message, onMessage }: { c: CaseDto; kind: 'przegrana' | 'odrzucona'; value: boolean; onChange: (v: boolean) => void; message: string; onMessage: (v: string) => void }) {
  const email = c.client.emails[0];
  const standard =
    kind === 'przegrana'
      ? 'Zgodnie z Państwa decyzją zamykamy zapytanie (po ofercie: sprawę) „tytuł” (adres). Dziękujemy za kontakt…'
      : 'Informujemy, że Państwa zapytanie „tytuł” (adres) zostało zamknięte. W razie pytań prosimy o kontakt.';
  return (
    <Stack gap={6}>
      <Checkbox
        label={email ? `Powiadom klienta e-mailem (${email})` : 'Powiadom klienta e-mailem'}
        description={email ? 'Wiadomość od firmy sprawy — bez powodu i szczegółów zamknięcia' : 'Klient nie ma adresu e-mail — uzupełnij go w karcie klienta'}
        disabled={!email}
        checked={value && !!email}
        onChange={(e) => onChange(e.currentTarget.checked)}
      />
      {value && email && <Textarea label="Treść wiadomości (opcjonalnie)" description={`Bez treści — wiadomość standardowa: ${standard}`} autosize minRows={2} value={message} onChange={(e) => onMessage(e.currentTarget.value)} />}
    </Stack>
  );
}

export function CloseForm({ c, onDone }: { c: CaseDto; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(false);
  const [message, setMessage] = useState('');
  const m = useCaseMutation(c.id, () => api<CaseDto>(`/cases/${c.id}/close`, { method: 'POST', body: { kind: 'odrzucona', reason, notifyClient: notify, clientMessage: message } }), () => {
    notifyOk(notify ? 'Zapytanie odrzucone — wiadomość do klienta w kolejce wysyłki' : 'Zapytanie odrzucone');
    onDone();
  });
  return (
    <Stack>
      <Text size="sm">Zapytanie zostanie zamknięte (np. spam, poza obszarem działania). Rezygnację klienta lub wybór konkurencji zamknij jako przegraną. Sprawę można później wznowić.</Text>
      <TextInput label="Powód" required description={`${minHint(reason)} · widoczny tylko w CRM`} value={reason} onChange={(e) => setReason(e.currentTarget.value)} data-autofocus />
      <NotifyClient c={c} kind="odrzucona" value={notify} onChange={setNotify} message={message} onMessage={setMessage} />
      <Actions onCancel={onDone} onOk={() => m.mutate(undefined)} loading={m.isPending} okLabel="Odrzuć zapytanie" danger disabled={reason.trim().length < 3} />
    </Stack>
  );
}

/**
 * QA P5: przed zamknięciem jako przegrana — wydane oferty czekające na decyzję i aktywne linki klienta. Linki zostaną
 * unieważnione (z wpisem w historii), wersje ofert zostają bez zmian (strona klienta pokaże „Oferta nieaktualna”).
 */
function IssuedOffersWarning({ c }: { c: CaseDto }) {
  const me = useMe();
  const on = can(me.data, 'oferty.podglad');
  const offers = useQuery({ queryKey: ['case-offers', c.id], queryFn: () => api<OfferListItemDto[]>(`/cases/${c.id}/offers`), enabled: on });
  const shares = useQuery({ queryKey: ['case-shares', c.id], queryFn: () => api<ShareDto[]>(`/cases/${c.id}/shares`), enabled: on });
  const issued = (offers.data ?? []).filter((o) => ['wydany', 'do_zmiany'].includes(o.latest.effectiveStatus));
  const now = new Date().toISOString();
  const links = (shares.data ?? []).filter((s) => !s.revokedAt && s.expiresAt > now);
  if (!issued.length && !links.length) return null;
  return (
    <Alert color="yellow" variant="light" title="Wydane oferty i linki dla klienta">
      <List size="sm" spacing={2}>
        {issued.map((o) => (
          <List.Item key={o.id}>Oferta {o.latest.issuedNumber ?? o.number} — czeka na decyzję klienta (zostaje wydana; klient zobaczy „Oferta nieaktualna”)</List.Item>
        ))}
        {links.map((s) => (
          <List.Item key={s.id}>
            Link {s.versions.map((v) => v.issuedNumber).join(', ')} dla {s.recipientEmail} — zostanie unieważniony
          </List.Item>
        ))}
      </List>
    </Alert>
  );
}

/**
 * P7: zamknięcie jako przegrana (rezygnacja, konkurencja…) — utracona sprzedaż. Powód podpowiadany z oznaczenia
 * przegranych zakresów; zakresy jeszcze otwarte zostaną oznaczone jako przegrane z tym powodem.
 */
export function LostCloseForm({ c, onDone }: { c: CaseDto; onDone: () => void }) {
  const marked = c.scopes.filter((s) => s.salesStage === 'przegrany' && s.lostReason);
  const live = c.scopes.filter((s) => s.salesStage !== 'przegrany');
  const trades = useTrades();
  const [lostReason, setLostReason] = useState<LostReason | null>(marked[marked.length - 1]?.lostReason ?? null);
  const [reason, setReason] = useState(marked[marked.length - 1]?.stageNote ?? '');
  const [notify, setNotify] = useState(false);
  const [message, setMessage] = useState('');
  const signed = c.scopes.some((s) => s.salesStage === 'umowa_podpisana');
  const m = useCaseMutation(c.id, () => api<CaseDto>(`/cases/${c.id}/close`, { method: 'POST', body: { kind: 'przegrana', lostReason, reason, notifyClient: notify, clientMessage: message } }), () => {
    notifyOk(notify ? 'Sprawa zamknięta jako przegrana — wiadomość do klienta w kolejce wysyłki' : 'Sprawa zamknięta jako przegrana');
    onDone();
  });
  return (
    <Stack>
      <Text size="sm">Sprawa trafi do utraconej sprzedaży z wybranym powodem. Sprawę można później wznowić.</Text>
      {signed && <Alert color="red" variant="light">Zakres ma podpisaną umowę — rezygnacja wymaga anulowania umowy.</Alert>}
      {live.length > 0 && !signed && (
        <Alert color="yellow" variant="light">
          Zakresy jeszcze otwarte ({live.map((s) => trades.data?.find((t) => t.code === s.tradeCode)?.name ?? s.tradeCode).join(', ')}) zostaną oznaczone jako przegrane z tym powodem.
        </Alert>
      )}
      <IssuedOffersWarning c={c} />
      <Select label="Powód" required data={opts(LOST_REASONS)} value={lostReason} onChange={(v) => setLostReason(v as LostReason | null)} allowDeselect={false} data-autofocus />
      <TextInput label="Szczegóły (opcjonalnie)" description="Widoczne tylko w CRM — nie trafiają do klienta" placeholder="np. wybrał ofertę konkurencji tańszą o 10%" value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
      <NotifyClient c={c} kind="przegrana" value={notify} onChange={setNotify} message={message} onMessage={setMessage} />
      <Actions onCancel={onDone} onOk={() => m.mutate(undefined)} loading={m.isPending} okLabel="Zamknij jako przegraną" danger disabled={!lostReason || signed} />
    </Stack>
  );
}

export function AddScopeForm({ c, onDone }: { c: CaseDto; onDone: () => void }) {
  const trades = useTrades();
  const companies = useCompanies();
  const used = new Set(c.scopes.map((s) => s.tradeCode));
  const [tradeCode, setTradeCode] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const m = useCaseMutation(c.id, () => api<CaseDto>(`/cases/${c.id}/scopes`, { method: 'POST', body: { tradeCode, companyId, description } }), () => {
    notifyOk('Dodano zakres prac');
    onDone();
  });
  return (
    <Stack>
      <Select
        label="Branża"
        data={(trades.data ?? []).filter((t) => t.active && !used.has(t.code)).map((t) => ({ value: t.code, label: t.name }))}
        value={tradeCode}
        onChange={(v) => {
          setTradeCode(v);
          setCompanyId(trades.data?.find((t) => t.code === v)?.defaultCompanyId ?? null);
        }}
      />
      <Select label="Firma realizująca i rozliczająca" data={(companies.data ?? []).map((co) => ({ value: co.id, label: co.shortName }))} value={companyId} onChange={setCompanyId} />
      <Textarea label="Opis zakresu" autosize minRows={2} value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
      <Actions onCancel={onDone} onOk={() => m.mutate(undefined)} loading={m.isPending} okLabel="Dodaj zakres" disabled={!tradeCode || !companyId} />
    </Stack>
  );
}

export function ScopeDescriptionForm({ c, scope, onDone }: { c: CaseDto; scope: ScopeDto; onDone: () => void }) {
  const [description, setDescription] = useState(scope.description);
  const m = useCaseMutation(c.id, () => api<CaseDto>(`/scopes/${scope.id}`, { method: 'PUT', body: { description } }), onDone);
  return (
    <Stack>
      <Textarea label="Opis zakresu prac" autosize minRows={3} value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
      <Actions onCancel={onDone} onOk={() => m.mutate(undefined)} loading={m.isPending} />
    </Stack>
  );
}

export function ScopeCompanyForm({ c, scope, onDone }: { c: CaseDto; scope: ScopeDto; onDone: () => void }) {
  const companies = useCompanies();
  const [companyId, setCompanyId] = useState<string | null>(scope.companyId);
  const [reason, setReason] = useState('');
  const m = useCaseMutation(c.id, () => api<CaseDto>(`/scopes/${scope.id}/company`, { method: 'PUT', body: { companyId, reason } }), onDone);
  return (
    <Stack>
      <Alert variant="light" color="blue">
        Zmiana jest możliwa, dopóki zakres nie ma wydanych dokumentów (oferty, umowy). Po wydaniu dokumentu korekta firmy wymaga wycofania dokumentu i wystawienia go w drugiej firmie.
      </Alert>
      <Select label="Firma" data={(companies.data ?? []).map((co) => ({ value: co.id, label: co.shortName }))} value={companyId} onChange={setCompanyId} allowDeselect={false} />
      <TextInput label="Powód zmiany" required description={minHint(reason)} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
      <Actions onCancel={onDone} onOk={() => m.mutate(undefined)} loading={m.isPending} disabled={!companyId || companyId === scope.companyId || reason.trim().length < 3} />
    </Stack>
  );
}

export function ScopeStageForm({ c, scope, action, onDone }: { c: CaseDto; scope: ScopeDto; action: 'przegrany' | 'wstrzymaj' | 'wznow'; onDone: () => void }) {
  const [reason, setReason] = useState<LostReason | null>(null);
  const [until, setUntil] = useState('');
  const [note, setNote] = useState('');
  const body = action === 'przegrany' ? { action, reason, note } : action === 'wstrzymaj' ? { action, until: until || null, note } : { action, note };
  const m = useCaseMutation(c.id, () => api<CaseDto>(`/scopes/${scope.id}/stage`, { method: 'POST', body }), onDone);
  return (
    <Stack>
      {action === 'wstrzymaj' && <HoldEffect path={`/scopes/${scope.id}/hold-preview`} />}
      {action === 'przegrany' && <Select label="Powód" data={opts(LOST_REASONS)} value={reason} onChange={(v) => setReason(v as LostReason)} />}
      {action === 'wstrzymaj' && <TextInput label="Wraca do tematu (opcjonalnie)" type="date" value={until} onChange={(e) => setUntil(e.currentTarget.value)} />}
      <TextInput label="Uwagi" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
      <Actions
        onCancel={onDone}
        onOk={() => m.mutate(undefined)}
        loading={m.isPending}
        okLabel={action === 'przegrany' ? 'Oznacz jako przegrany' : action === 'wstrzymaj' ? 'Wstrzymaj' : 'Wznów'}
        disabled={action === 'przegrany' && !reason}
      />
    </Stack>
  );
}

/**
 * Co wstrzymanie zrobi z pracami (docs/10 §16): nierozpoczęte realizacje — anulowane, inne zaplanowane prace — z planu
 * do kolejki (ekipa dostaje „Odwołano”), rozpoczęte bez zmian. Pokazywane w oknie wstrzymania zakresu i sprawy.
 */
export const useHoldPreview = (path: string) => useQuery({ queryKey: ['hold-preview', path], queryFn: () => api<WorkHoldPreviewDto>(path) });

/** QA qa10310111 3.11: terminy odwoływanych prac, o których klient dostał e-mail — „30.10” (DD.MM z treści terminu). */
export function clientKnownTerms(p: WorkHoldPreviewDto | undefined): string[] {
  return [...(p?.cancelled ?? []), ...(p?.planned ?? [])].filter((w) => !!w.clientTerm).map((w) => w.clientTerm!.match(/\d{2}\.\d{2}/)?.[0] ?? w.clientTerm!);
}

export function HoldEffect({ path }: { path: string }) {
  const q = useHoldPreview(path);
  const when = (w: HeldWorkDto) => [w.dateFrom ? (w.dateTo && w.dateTo !== w.dateFrom ? `${datePl(w.dateFrom)} – ${datePl(w.dateTo)}` : datePl(w.dateFrom)) : '', w.clientTerm ? 'klient zna termin' : ''].filter(Boolean).join(' · ');
  if (!q.data || (!q.data.cancelled.length && !q.data.planned.length && !q.data.started.length)) return null;
  return (
    <Alert color="yellow" variant="light" title="Prace w tej sprawie">
      <Stack gap={4}>
        {q.data.cancelled.length > 0 && (
          <Text size="sm">
            Realizacje zostaną <b>anulowane</b> (ekipa dostanie informację „Odwołano”, termin się zwolni): {q.data.cancelled.map((w) => `${w.label} ${w.number}${when(w) ? ` (${when(w)})` : ''}`).join(', ')}. Po wznowieniu koordynator zaplanuje realizację od nowa.
          </Text>
        )}
        {q.data.planned.length > 0 && (
          <Text size="sm">
            Wrócą z planu do kolejki (ekipa dostanie informację „Odwołano”, termin się zwolni): {q.data.planned.map((w) => `${w.label} ${w.number}${when(w) ? ` (${when(w)})` : ''}`).join(', ')}. Po wznowieniu wrócą do planowania.
          </Text>
        )}
        {q.data.started.length > 0 && (
          <Text size="sm">
            Rozpoczęte — bez zmian (zdecyduj w planowaniu): {q.data.started.map((w) => `${w.label} ${w.number}`).join(', ')}.
          </Text>
        )}
      </Stack>
    </Alert>
  );
}
